import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
    TextField,
    Button,
    Grid,
    Paper,
    MenuItem,
    Box,
    Divider,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Tooltip,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
    getToDoPrioritys,
    getToDoStatus,
    getToDoUser,
} from 'core/API/todo';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { NavLink, useHistory, useParams } from 'react-router-dom';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { useWorkplaceUser } from 'module/documents/utils';
import styles from './AddToDo.module.scss';
import { getSingleBroadcastMessage, PostBroadcast } from 'core/API/broadcast';
import { apiService } from 'core/services/apiService';
import { usePermissionCheck } from 'core/hooks/usePermissionCheck';

// Add RouteParams type for useParams
type RouteParams = { id?: string };

const AddToDo: React.FC = () => {
    const { showToast } = useToastContext();
    const queryClient = useQueryClient();
    const history = useHistory();
    const workplace = useWorkplaceUser();
    const { id } = useParams<RouteParams>();

    // Broadcast permission checks (module-level, like Logbook)
    const {
        hasModuleViewPermission,
        hasModuleEditPermission,
        hasModuleExecutePermission,
    } = usePermissionCheck();
    const canViewBroadcastMy = hasModuleViewPermission('Broadcast - My');
    const canViewBroadcastAll = hasModuleViewPermission('Broadcast - All');
    const canViewBroadcastOrganization = hasModuleViewPermission('Broadcast - Organization');
    const canEditBroadcastMy = hasModuleEditPermission('Broadcast - My');
    const canEditBroadcastAll = hasModuleEditPermission('Broadcast - All');
    const canEditBroadcastOrganization = hasModuleEditPermission('Broadcast - Organization');
    const canExecuteBroadcastMy = hasModuleExecutePermission('Broadcast - My');
    const canExecuteBroadcastAll = hasModuleExecutePermission('Broadcast - All');
    const canExecuteBroadcastOrganization = hasModuleExecutePermission('Broadcast - Organization');

    // Check if user can create/edit broadcasts
    const canCreateBroadcast = canEditBroadcastMy || canEditBroadcastAll || canEditBroadcastOrganization || 
                               canExecuteBroadcastMy || canExecuteBroadcastAll || canExecuteBroadcastOrganization;
    const canViewBroadcast = canViewBroadcastMy || canViewBroadcastAll || canViewBroadcastOrganization;

    // const userId = workplace?.data.user.id;
    const workplaceId = parseInt(localStorage.getItem('workplace-id') || '1');

    const [form, setForm] = useState({
        title: "",
        from: "",
        to: "",
        noteMessage: '',
        priorityId: 2, // Default to Medium priority (ID 2)
    });

    const [isFormDirty, setIsFormDirty] = useState(false);
    const [openBackDialog, setOpenBackDialog] = useState(false);
    const [receiverType, setReceiverType] = useState<'User' | 'Broadcast Group'>('User');
    const [selectedValue, setSelectedValue] = useState<any>('');
    const [currentUserFullName, setCurrentUserFullName] = useState<string>('');
    const [errors, setErrors] = useState<{
        title?: string;
        assigned?: string;
        priority?: string;
        content?: string;
    }>({});
    const [value, setValue] = useState("");
    const [openUrlDialog, setOpenUrlDialog] = useState(false);
    const [urlInput, setUrlInput] = useState("");
    const [urlDisplayText, setUrlDisplayText] = useState("");


    // Fetch current user data to get full name
    const currentUserId = Number(localStorage.getItem('userId') || '0');
    const { data: currentUserData } = useQuery(
        ['current-user', currentUserId],
        async () => {
            if (!currentUserId) return null;
            const response = apiService.responseHandler(await apiService.get(`/users/${currentUserId}`));
            return response;
        },
        {
            enabled: !!currentUserId,
            onSuccess: (user) => {
                if (user) {
                    const fullName = `${user.firstName || ''} ${user.middleName || ''} ${user.lastName || ''}`.trim().replace(/\s+/g, ' ');
                    setCurrentUserFullName(fullName);
                    setForm((prev) => ({ ...prev, from: fullName }));
                }
            }
        }
    );

    // Fetch broadcast groups
    const { data: broadcastGroupsData, isLoading: isBroadcastGroupsLoading } = useQuery(
        ['broadcast-groups', workplaceId],
        async () => {
            const response = apiService.responseHandler(await apiService.get('/broadcast_groups', {
                params: {
                    workplace: workplaceId || 1,
                    page: 1,
                    itemsPerPage: 100,
                    pagination: false,
                },
            }));
            if (response?.member && Array.isArray(response.member)) {
                return { member: response.member };
            }
            if (Array.isArray(response)) {
                return { member: response };
            }
            return { member: [] };
        },
        {
            enabled: !!workplaceId,
        }
    );

    const broadcastGroups = broadcastGroupsData?.member || [];

    // Fetch existing todo data
        const { data: existingBroadcast, isLoading: isLoadingTodo, error: todoError } = useQuery(
            ['todo', id],
            () => getSingleBroadcastMessage(Number(id)),
            {
                enabled: !!id,
                onSuccess: (todo) => {
                    // Determine receiver type
                    const hasReceiverUser = !!todo.receiverUser;
                    const hasReceiverGroup = !!todo.receiverGroup;
                    const receiverTypeValue = hasReceiverUser ? 'User' : 'Broadcast Group';
                    
                    setReceiverType(receiverTypeValue);
                    
                    // Pre-fill form with existing data
                    setForm({
                        title: todo.title || '',
                        from: todo.sender ? `${todo.sender.firstName} ${todo.sender.lastName}` : '',
                        to: '',
                        noteMessage: '',
                        priorityId: todo.priority?.id || (typeof todo.priority === 'string' ? parseInt(todo.priority.split('/').pop()) : 1)
                    });
                    setValue(todo.content);
                    
                    if (hasReceiverUser) {
                        setSelectedValue(todo.receiverUser.id);
                    } else if (hasReceiverGroup) {
                        const groupId = typeof todo.receiverGroup === 'string' 
                            ? parseInt(todo.receiverGroup.split('/').pop() || '0')
                            : todo.receiverGroup?.id || 0;
                        setSelectedValue(groupId);
                    }
                }
            }
        );

    const { mutate: postOrUpdateToDo, isLoading } = useMutation(
        (data: any) => PostBroadcast(data),
        {
            onSuccess: () => {
                showToast('success', 'Broadcast created successfully!');

                // Only reset form if it's a new todo (not editing)
                if (!id) {
                    setForm({
                        title: '',
                        from: currentUserFullName,
                        to: '',
                        noteMessage: '',
                        priorityId: 2, // Default to Medium priority (ID 2)
                    });
                    setSelectedValue('');
                    setReceiverType('User');
                }

                resetFormDirty();
                try {
                    const workplaceIdLS = Number(localStorage.getItem('workplace-id') || '1');
                    queryClient.invalidateQueries(['todos', workplaceIdLS]);
                } catch (e) {
                    console.warn('Failed to invalidate todos cache after add', e);
                }
                history.push(ROUTES.BROADCAST);
            },
            onError: (error) => {
                console.error('Todo operation failed:', error);
                showToast('error', 'Failed to create Broadcast');
            },
        }
    );

    const { data: prioritysList, isLoading: isPrioritiesLoading } = useQuery(
        ['todo-priorities', workplaceId],
        () => getToDoPrioritys(),
        {
            enabled: true,
            refetchOnMount: 'always',
            refetchOnWindowFocus: false,
            staleTime: 0,
        }
    );

    const { data: statusList, isLoading: isStatusesLoading } = useQuery(
        ['todo-statuses', workplaceId],
        () => getToDoStatus(),
        {
            enabled: true,
            refetchOnMount: 'always',
            refetchOnWindowFocus: false,
            staleTime: 0,
        }
    );

    const { data: usersList, isLoading: isUsersLoading } = useQuery(
        ['todo-users', workplaceId],
        () => getToDoUser(),
        {
            enabled: !!workplaceId,
            refetchOnMount: 'always',
            refetchOnWindowFocus: false,
            staleTime: 0,
        }
    );

    // Fetch groups from API

    // Normalize API responses to support different shapes
    const normalizedPriorities = Array.isArray(prioritysList?.member)
        ? prioritysList.member
        : Array.isArray(prioritysList?.['hydra:member'])
            ? prioritysList['hydra:member']
            : Array.isArray(prioritysList?.items)
                ? prioritysList.items
                : Array.isArray(prioritysList?.data)
                    ? prioritysList.data
                    : Array.isArray(prioritysList)
                        ? prioritysList
                        : [];

    const normalizedStatuses = Array.isArray(statusList?.member)
        ? statusList.member
        : Array.isArray(statusList?.['hydra:member'])
            ? statusList['hydra:member']
            : Array.isArray(statusList?.items)
                ? statusList.items
                : Array.isArray(statusList?.data)
                    ? statusList.data
                    : Array.isArray(statusList)
                        ? statusList
                        : [];

    const noPriorities = !isPrioritiesLoading && normalizedPriorities.length === 0;
    const noStatuses = !isStatusesLoading && normalizedStatuses.length === 0;

    // Normalize users response to array of users (support array or { member: [] } shapes)
    const normalizedUsers = Array.isArray(usersList)
        ? usersList
        : Array.isArray((usersList as any)?.member)
            ? (usersList as any).member
            : Array.isArray((usersList as any)?.['hydra:member'])
                ? (usersList as any)['hydra:member']
                : Array.isArray((usersList as any)?.data)
                    ? (usersList as any).data
                    : [];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setIsFormDirty(true);
        setForm((prev) => ({ ...prev, [name]: value }));

        // Clear per-field errors on change
        if (name === 'description') {
            setErrors((prev) => ({ ...prev, description: '' }));
        }
        if (name === 'dueDate') {
            setErrors((prev) => ({ ...prev, dueDate: '' }));
        }
        if (name === 'noteMessage') {
            setErrors((prev) => ({ ...prev, noteMessage: '' }));
        }
    };

    // Reset form dirty state when form is successfully submitted
    const resetFormDirty = () => {
        setIsFormDirty(false);
    };

    const handleReceiverTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setReceiverType(e.target.value as 'User' | 'Broadcast Group');
        setIsFormDirty(true);
        setSelectedValue('');
        setErrors((prev) => ({ ...prev, assigned: '' }));
    };

    const handleBackClick = () => {
        // Don't show unsaved changes dialog in readonly/view mode
        if (id) {
            history.push(ROUTES.BROADCAST);
            return;
        }
        
        if (isFormDirty) {
            setOpenBackDialog(true);
        } else {
            history.push(ROUTES.BROADCAST);
        }
    };

    const handleDialogClose = (save: boolean) => {
        setOpenBackDialog(false);
        if (save) {
            handleSubmit({ preventDefault: () => {} } as React.FormEvent);
        } else {
            history.push(ROUTES.BROADCAST);
        }
    };

    // Enhanced validation for edit mode
    const validateForm = () => {
        const nextErrors: {
            title?: string;
            assigned?: string;
            priority?: string;
            content?: string;
        } = {};
        
        // Validate title
        if (!form.title.trim()) {
            nextErrors.title = 'Title is required';
        } else if (form.title.length > 250) {
            nextErrors.title = 'Title must be ≤ 250 characters';
        }
        
        // Validate receiver (To field)
        if (!selectedValue) {
            nextErrors.assigned = `Please select a ${receiverType === 'User' ? 'user' : 'broadcast group'}`;
        }
        
        // Validate priority
        if (!form.priorityId || form.priorityId === 0) {
            nextErrors.priority = 'Priority is required';
        }
        
        // Validate content (ReactQuill editor)
        // Check if there's text content, images, or links
        const contentText = value.replace(/<[^>]*>/g, '').trim(); // Strip HTML tags
        const hasImages = /<img[^>]*>/i.test(value); // Check for image tags
        const hasLinks = /<a[^>]*href[^>]*>/i.test(value); // Check for link tags
        
        if (!contentText && !hasImages && !hasLinks) {
            nextErrors.content = 'Content is required';
        }
        
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Use enhanced validation
        if (!validateForm()) {
            return;
        }

        const workplaceIdLS = Number(localStorage.getItem('workplace-id') || '1');

        const body: any = {
            receiverUser: receiverType === 'User' ? `/api/users/${selectedValue}` : null,
            receiverGroup: receiverType === 'Broadcast Group' ? `/api/broadcast_groups/${selectedValue}` : null,
            workplace: `/api/workplaces/${workplaceIdLS}`,
            title: form.title,
            content: value,
            priority: `/api/priorities/${form.priorityId}`,
            attachments: [],
        };

        try {
            postOrUpdateToDo(body);
        } catch (error) {
            console.error('Error submitting todo:', error);
            showToast('error', 'Failed to submit todo. Please try again.');
        }
    };

    const handleLinkClick = useCallback(() => {
        setUrlInput("");
        setUrlDisplayText("");
        setOpenUrlDialog(true);
    }, []);

    const handleUrlDialogSubmit = () => {
        if (urlInput.trim() && quillRef.current) {
            const quill = quillRef.current.getEditor();
            const range = quill.getSelection(true);
            const displayText = urlDisplayText.trim() || urlInput.trim();
            
            if (range) {
                // If there's selected text, format it as a link
                if (range.length > 0) {
                    quill.formatText(range.index, range.length, 'link', urlInput.trim());
                } else {
                    // Otherwise, insert new text with link format
                    quill.insertText(range.index, displayText, 'link', urlInput.trim());
                    quill.setSelection(range.index + displayText.length);
                }
            }
        }
        setOpenUrlDialog(false);
        setUrlInput("");
        setUrlDisplayText("");
    };

    const quillRef = useRef<ReactQuill>(null);

    const modules = useMemo(() => {
        const toolbarOptions = [
            [{ header: [1, 2, false] }],
            ["bold", "italic", "underline"],
            ["link", "image"],
        ];
        
        return {
            toolbar: {
                container: toolbarOptions,
                handlers: {
                    link: handleLinkClick,
                },
            },
            clipboard: {
                matchVisual: false
            }
        };
    }, [handleLinkClick]);

    // Add tooltips to URL and Image buttons
    useEffect(() => {
        const addTooltips = () => {
            const linkButton = document.querySelector('.ql-link') as HTMLElement;
            const imageButton = document.querySelector('.ql-image') as HTMLElement;
            
            if (linkButton && !linkButton.getAttribute('title')) {
                linkButton.setAttribute('title', 'Add URL');
            }
            
            if (imageButton && !imageButton.getAttribute('title')) {
                imageButton.setAttribute('title', 'Add Image');
            }
        };

        // Add tooltips after a short delay to ensure toolbar is rendered
        const timer = setTimeout(addTooltips, 100);
        
        // Also try to add tooltips when the component updates
        const observer = new MutationObserver(addTooltips);
        const toolbar = document.querySelector('.ql-toolbar');
        if (toolbar) {
            observer.observe(toolbar, { childList: true, subtree: true });
        }

        return () => {
            clearTimeout(timer);
            observer.disconnect();
        };
    }, [value]);

    // If user doesn't have permission to view or create broadcasts, show error message
    if (!canViewBroadcast && !canCreateBroadcast) {
        return (
            <Paper elevation={1} style={{ padding: '24px', maxWidth: '100%', margin: '0 auto' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Button
                        style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }}
                        onClick={() => history.push(ROUTES.BROADCAST)}
                    >
                        Back
                    </Button>
                </Box>
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6" color="error">
                        You do not have permissions to view or create broadcasts.
                    </Typography>
                </Box>
            </Paper>
        );
    }

    return (
        <Paper elevation={1} style={{ padding: '24px', maxWidth: '100%', margin: '0 auto' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Button
                    style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }}
                    onClick={handleBackClick}
                >
                    Back
                </Button>
                {!id && canCreateBroadcast ? (<Button
                    type="submit"
                    style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white', padding: '5px 12px' }}
                    disabled={isLoading}
                    onClick={handleSubmit}
                >
                    {isLoading ? 'Sending...' : 'Send'}
                </Button>) : ""}
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="h6" mb={2} className={styles.schedulerTitle}>
                {id ? 'View Broadcast' : 'Create Broadcast'}
            </Typography>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            name="from"
                            label="From"
                            fullWidth
                            required
                            value={form.from}
                            InputProps={{
                                readOnly: true,
                            }}
                            disabled={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            name="priorityId"
                            select
                            label="Priority"
                            fullWidth
                            required
                            value={form.priorityId}
                            onChange={(e) => {
                                const value = Number((e.target as HTMLInputElement).value);
                                setForm((prev) => ({ ...prev, priorityId: value }));
                                setIsFormDirty(true);
                                setErrors((prev) => ({ ...prev, priority: '' }));
                            }}
                            disabled={!!id}
                            error={!!errors.priority}
                            helperText={errors.priority}
                            SelectProps={{
                                MenuProps: {
                                    PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } },
                                    disableScrollLock: true,
                                },
                            }}
                        >
                            {isPrioritiesLoading ? (
                                <MenuItem disabled value="">
                                    Loading priorities...
                                </MenuItem>
                            ) : noPriorities ? (
                                <MenuItem disabled value="">
                                    No priorities found
                                </MenuItem>
                            ) : (
                                normalizedPriorities.map((priority: any) => (
                                    <MenuItem value={priority.id} key={priority.id}>
                                        {priority.label ?? priority.name}
                                    </MenuItem>
                                ))
                            )}
                        </TextField>
                    </Grid>

                    {!id && (
                        <Grid item xs={12}>
                            <FormControl component="fieldset" sx={{ mb: 2 }}>
                                <RadioGroup
                                    row
                                    value={receiverType}
                                    onChange={handleReceiverTypeChange}
                                    sx={{ 
                                        display: 'flex', 
                                        gap: 3,
                                        '& .MuiRadio-root': {
                                            color: 'rgb(68, 99, 136)',
                                            '&.Mui-checked': {
                                                color: 'rgb(68, 99, 136)',
                                            },
                                        },
                                    }}
                                >
                                    <FormControlLabel 
                                        value="User" 
                                        control={<Radio />} 
                                        label="User"
                                    />
                                    <FormControlLabel 
                                        value="Broadcast Group" 
                                        control={<Radio />} 
                                        label="Broadcast Group"
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Box className={styles.selectionBox}>
                            <TextField
                                select
                                label='To'
                                value={selectedValue || ''}
                                onChange={(e) => {
                                    const value = Number((e.target as HTMLInputElement).value);
                                    setSelectedValue(value);
                                    setIsFormDirty(true);
                                    setErrors((prev) => ({ ...prev, assigned: '' }));
                                }}
                                fullWidth
                                required
                                disabled={!!id}
                                error={!!errors.assigned}
                                helperText={errors.assigned}
                            >
                                {!selectedValue && (
                                    <MenuItem value="" disabled>
                                        Select {receiverType === 'User' ? 'User' : 'Broadcast Group'}
                                    </MenuItem>
                                )}
                                {receiverType === 'User' ? (
                                    isUsersLoading ? (
                                        <MenuItem disabled value="">
                                            Loading users...
                                        </MenuItem>
                                    ) : (
                                        (normalizedUsers || [])
                                            .sort((a: any, b: any) => {
                                                const firstNameA = (a.firstName || '').toLowerCase();
                                                const firstNameB = (b.firstName || '').toLowerCase();
                                                const lastNameA = (a.lastName || '').toLowerCase();
                                                const lastNameB = (b.lastName || '').toLowerCase();

                                                if (firstNameA !== firstNameB) {
                                                    return firstNameA.localeCompare(firstNameB);
                                                }
                                                return lastNameA.localeCompare(lastNameB);
                                            })
                                            .map((item: any) => (
                                                <MenuItem key={item.id} value={item.id}>
                                                    {`${item.firstName || ''} ${item.lastName || ''}`.trim()}
                                                </MenuItem>
                                            ))
                                    )
                                ) : (
                                    isBroadcastGroupsLoading ? (
                                        <MenuItem disabled value="">
                                            Loading broadcast groups...
                                        </MenuItem>
                                    ) : (
                                        (broadcastGroups || [])
                                            .map((group: any) => (
                                                <MenuItem key={group.id} value={group.id}>
                                                    {group.groupName || `Group ${group.id}`}
                                                </MenuItem>
                                            ))
                                    )
                                )}
                            </TextField>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            name="title"
                            label="Subject"
                            fullWidth
                            required
                            value={form.title}
                            onChange={handleChange}
                            inputProps={{ maxLength: 250 }}
                            disabled={!!id}
                            error={!!errors.title}
                            helperText={errors.title}
                        />
                    </Grid>



                    <Grid item xs={12}>
                        <Box sx={{ 
                            '& .ql-editor': {
                                fontSize: '1rem',
                                fontFamily: 'inherit',
                                minHeight: '400px',
                            },
                            '& .ql-container': {
                                fontSize: '1rem',
                                fontFamily: 'inherit',
                                minHeight: '400px',
                            },
                            '& .ql-toolbar': {
                                borderTop: '1px solid #ccc',
                                borderLeft: '1px solid #ccc',
                                borderRight: '1px solid #ccc',
                                ...(id && {
                                    pointerEvents: 'none',
                                    opacity: 0.6,
                                }),
                            },
                            '& .ql-container.ql-snow': {
                                borderBottom: '1px solid #ccc',
                                borderLeft: '1px solid #ccc',
                                borderRight: '1px solid #ccc',
                            },
                        }}>
                            <ReactQuill
                                key={id || 'new-broadcast'}
                                ref={quillRef}
                                value={value}
                                onChange={(content) => {
                                    setValue(content);
                                    setIsFormDirty(true);
                                    // Clear error when user adds content (text, images, or links)
                                    const contentText = content.replace(/<[^>]*>/g, '').trim();
                                    const hasImages = /<img[^>]*>/i.test(content);
                                    const hasLinks = /<a[^>]*href[^>]*>/i.test(content);
                                    
                                    if (contentText || hasImages || hasLinks) {
                                        setErrors((prev) => ({ ...prev, content: '' }));
                                    }
                                }}
                                modules={modules}
                                theme="snow"
                                 style={{
                                    backgroundColor: 'white',
                                    height: '500px',
                                }}
                                placeholder="Enter your message here..."
                                readOnly={!!id}
                            />
                        </Box>
                        {errors.content && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {errors.content}
                            </Typography>
                        )}
                    </Grid>
                </Grid>
            </form>

            <Dialog open={openBackDialog} onClose={() => handleDialogClose(false)}>
                <DialogTitle>Unsaved Changes</DialogTitle>
                <DialogContent>
                    You have not sent your message. Would you like to send before leaving?
                </DialogContent>
                <DialogActions>
                    <Button
                        style={{
                            backgroundColor: 'rgb(68, 99, 136)',
                            color: 'white',
                            padding: '5px 12px',
                        }}
                        onClick={() => handleDialogClose(false)}
                    >
                        Exit
                    </Button>
                    <Button
                        onClick={() => handleDialogClose(true)}
                        style={{
                            backgroundColor: 'rgb(68, 99, 136)',
                            color: 'white',
                            padding: '5px 12px',
                        }}
                    >
                        Send
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openUrlDialog} onClose={() => setOpenUrlDialog(false)}>
                <DialogTitle>Add URL</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: '400px', pt: 1 }}>
                        <TextField
                            label="URL"
                            fullWidth
                            required
                            value={urlInput}
                            onChange={(e) => setUrlInput(e.target.value)}
                            placeholder="https://example.com"
                        />
                        <TextField
                            label="Display Text"
                            fullWidth
                            value={urlDisplayText}
                            onChange={(e) => setUrlDisplayText(e.target.value)}
                            placeholder="Link text (optional)"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpenUrlDialog(false)}
                        style={{
                            color: 'rgb(68, 99, 136)',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUrlDialogSubmit}
                        disabled={!urlInput.trim()}
                        style={{
                            backgroundColor: 'rgb(68, 99, 136)',
                            color: 'white',
                            padding: '5px 12px',
                        }}
                    >
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default AddToDo;
